/**
 * CTRL_03 Configuration Panel - Model Provenance & Versioning
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Collapse, Button, Alert, Spin, Select, Form } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

const PROVENANCE_FIELDS = [
  { value: 'model_version', label: 'Model Version' },
  { value: 'training_date', label: 'Training Date' },
  { value: 'source_repo', label: 'Source Repository' },
  { value: 'data_lineage', label: 'Data Lineage' },
  { value: 'model_card_url', label: 'Model Card URL' },
  { value: 'git_commit_hash', label: 'Git Commit Hash' }
];

const VERSION_FORMATS = [
  { value: 'semver', label: 'Semantic Versioning (e.g., 1.2.3)', description: 'Major.Minor.Patch format' },
  { value: 'date', label: 'Date-based (e.g., 2025-11-04)', description: 'YYYY-MM-DD format' },
  { value: 'git_hash', label: 'Git Hash (e.g., abc123)', description: 'Short or full git commit hash' },
  { value: 'custom', label: 'Custom', description: 'Any format' }
];

function CTRL_03_ConfigPanel({ project, projectId, controlCode = 'CTRL_03' }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [configData, setConfigData] = useState({
    enabled: false,
    required_provenance_fields: ['model_version', 'training_date', 'source_repo'],
    require_version_control: true,
    version_format: 'semver',
    require_model_card: false,
    mlflow_integration_enabled: false,
    require_data_lineage: true
  });

  useEffect(() => {
    if (projectId) {
      fetchConfig();
    }
  }, [projectId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getControlConfig(projectId, controlCode);
      if (res.data) {
        setConfig(res.data);
        setConfigData({
          enabled: res.data.enabled || false,
          ...res.data.config
        });
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        control_code: controlCode,
        enabled: configData.enabled,
        config: {
          required_provenance_fields: configData.required_provenance_fields,
          require_version_control: configData.require_version_control,
          version_format: configData.version_format,
          require_model_card: configData.require_model_card,
          mlflow_integration_enabled: configData.mlflow_integration_enabled,
          require_data_lineage: configData.require_data_lineage
        }
      };

      await saveControlConfig(projectId, controlCode, payload);
      showSuccess('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <h3>CTRL_03: Model Provenance & Versioning Configuration</h3>
        <p style={{ color: '#666' }}>
          Track model origin, version, and training data lineage to ensure reproducibility and compliance
        </p>
      </div>

      <Collapse defaultActiveKey={configData.enabled ? ['1', '2'] : ['1']}>
        <Panel header="1. Enable Model Provenance Control" key="1">
          <Switch
            checked={configData.enabled}
            onChange={(checked) => updateConfig('enabled', checked)}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <p style={{ marginTop: '12px', color: '#666' }}>
            When enabled, inference and training events will be checked for model provenance metadata
          </p>
        </Panel>

        {configData.enabled && (
          <Panel header="2. Provenance Configuration" key="2">
            <Form layout="vertical">
              <Form.Item label="Required Provenance Fields">
                <Select
                  mode="multiple"
                  value={configData.required_provenance_fields || []}
                  onChange={(value) => updateConfig('required_provenance_fields', value)}
                  style={{ width: '100%' }}
                  placeholder="Select required fields"
                >
                  {PROVENANCE_FIELDS.map(field => (
                    <Option key={field.value} value={field.value}>
                      {field.label}
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Metadata fields that must be present for model events
                </p>
              </Form.Item>

              <Form.Item label="Version Format">
                <Select
                  value={configData.version_format}
                  onChange={(value) => updateConfig('version_format', value)}
                  style={{ width: '100%' }}
                >
                  {VERSION_FORMATS.map(format => (
                    <Option key={format.value} value={format.value}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{format.label}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{format.description}</div>
                      </div>
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Expected format for model version identifiers
                </p>
              </Form.Item>

              <Form.Item label="Version Control & Lineage">
                <div style={{ marginBottom: '16px' }}>
                  <Switch
                    checked={configData.require_version_control}
                    onChange={(checked) => updateConfig('require_version_control', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require version control for model code (Git)</span>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <Switch
                    checked={configData.require_data_lineage}
                    onChange={(checked) => updateConfig('require_data_lineage', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require training data lineage tracking</span>
                </div>

                <div>
                  <Switch
                    checked={configData.require_model_card}
                    onChange={(checked) => updateConfig('require_model_card', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require model card documentation</span>
                </div>
              </Form.Item>

              <Form.Item label="MLflow Integration">
                <Switch
                  checked={configData.mlflow_integration_enabled}
                  onChange={(checked) => updateConfig('mlflow_integration_enabled', checked)}
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Enable MLflow connector to automatically track model metadata
                </p>
              </Form.Item>
            </Form>
          </Panel>
        )}
      </Collapse>

      {configData.enabled && (
        <Alert
          message="Configuration Summary"
          description={
            <div>
              <div><strong>Required Fields:</strong> {configData.required_provenance_fields?.join(', ') || 'None'}</div>
              <div><strong>Version Format:</strong> {VERSION_FORMATS.find(f => f.value === configData.version_format)?.label || 'Not Set'}</div>
              <div><strong>Version Control:</strong> {configData.require_version_control ? 'Required' : 'Optional'}</div>
              <div><strong>Data Lineage:</strong> {configData.require_data_lineage ? 'Required' : 'Optional'}</div>
              <div><strong>Model Cards:</strong> {configData.require_model_card ? 'Required' : 'Optional'}</div>
              <div><strong>MLflow:</strong> {configData.mlflow_integration_enabled ? 'Enabled' : 'Disabled'}</div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: '16px' }}
        />
      )}

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
        style={{ marginTop: '16px' }}
        size="large"
      >
        Save Configuration
      </Button>
    </Card>
  );
}

export default CTRL_03_ConfigPanel;
